import React from "react";
import { Check, Plus } from "lucide-react";
import { LoadingSpinner, InlineLoadingState } from '../../../components/LoadingSpinner';
import type { OfferAttachment } from "../../../types";
import { formatLeadDocumentSize } from "../../projects/utils/leadDocumentFiles";

type AvailableDocument = {
  id: string;
  filename: string;
  mime_type: "application/pdf" | "image/png" | "image/jpeg";
  size: number;
  object_path: string;
  created_at: string;
};

type OfferAttachmentDocumentPickerProps = {
  leadRouteId: string;
  /** object_paths that are already attached — already-attached docs are disabled. */
  existingObjectPaths: ReadonlySet<string>;
  onAdd: (added: OfferAttachment[]) => void | Promise<void>;
  /** Re-fetch trigger: changes whenever the parent wants a fresh document list. */
  reloadKey?: number;
  /** When true, only PNG/JPEG documents are listed. */
  imagesOnly?: boolean;
  /** Remaining slots; extra selected images are ignored when adding. */
  maxImages?: number;
};

function isPdf(doc: AvailableDocument): boolean {
  return doc.mime_type === "application/pdf";
}

export const OfferAttachmentDocumentPicker: React.FC<OfferAttachmentDocumentPickerProps> = ({
  leadRouteId,
  existingObjectPaths,
  onAdd,
  reloadKey = 0,
  imagesOnly = false,
  maxImages,
}) => {
  const [docs, setDocs] = React.useState<AvailableDocument[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [adding, setAdding] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotice(null);
    setSelected(new Set());
    (async () => {
      try {
        const res = await fetch(
          `/api/leads/${encodeURIComponent(leadRouteId)}/offer-attachments/available-documents`,
          { credentials: "include" },
        );
        if (!res.ok) throw new Error(`Dokumente konnten nicht geladen werden (${res.status})`);
        const data = (await res.json()) as AvailableDocument[];
        if (!cancelled) {
          const next = Array.isArray(data) ? data : [];
          setDocs(imagesOnly ? next.filter((doc) => !isPdf(doc)) : next);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Fehler beim Laden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadRouteId, reloadKey, imagesOnly]);

  const pdfs = React.useMemo(() => docs.filter(isPdf), [docs]);
  const images = React.useMemo(() => docs.filter((d) => !isPdf(d)), [docs]);
  const isAttached = React.useCallback(
    (doc: AvailableDocument) => existingObjectPaths.has(doc.object_path),
    [existingObjectPaths],
  );

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (typeof maxImages === 'number' && next.size >= maxImages) return prev;
      else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    const selectedIds = Array.from(selected);
    const limitedIds =
      typeof maxImages === 'number' ? selectedIds.slice(0, Math.max(0, maxImages)) : selectedIds;
    if (limitedIds.length === 0) return;
    setAdding(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(
        `/api/leads/${encodeURIComponent(leadRouteId)}/offer-attachments/from-documents`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ document_ids: limitedIds }),
        },
      );
      const payload = (await res.json().catch(() => ({}))) as
        | OfferAttachment[]
        | {
            attachments?: OfferAttachment[];
            skipped_count?: number;
            skipped_not_found?: number;
            skipped_unsupported?: number;
            error?: string;
          };
      if (!res.ok) {
        const msg =
          (!Array.isArray(payload) && payload.error) ||
          `Hinzufügen fehlgeschlagen (${res.status})`;
        throw new Error(msg);
      }
      const attachmentsList = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.attachments)
          ? payload.attachments
          : [];
      const added = attachmentsList.filter((a) => !existingObjectPaths.has(a.object_path));
      if (added.length === 0) {
        throw new Error(
          "Alle ausgewählten Dokumente sind bereits angehängt oder konnten nicht hinzugefügt werden.",
        );
      }
      await onAdd(added);
      setSelected(new Set());
      if (!Array.isArray(payload)) {
        const skippedNotFound = payload.skipped_not_found ?? 0;
        const skippedUnsupported = payload.skipped_unsupported ?? 0;
        if (skippedNotFound > 0 && skippedUnsupported > 0) {
          setNotice(
            `${skippedNotFound + skippedUnsupported} Dokumente konnten nicht angehängt werden (${skippedNotFound} nicht gefunden, ${skippedUnsupported} nicht unterstützt).`,
          );
        } else if (skippedNotFound > 0) {
          setNotice(
            `${skippedNotFound} Dokument${skippedNotFound === 1 ? "" : "e"} wurde${skippedNotFound === 1 ? "" : "n"} nicht gefunden.`,
          );
        } else if (skippedUnsupported > 0) {
          setNotice(
            `${skippedUnsupported} Dokument${skippedUnsupported === 1 ? "" : "e"} konnte${skippedUnsupported === 1 ? "" : "n"} nicht angehängt werden (nur PDF, PNG, JPEG).`,
          );
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Hinzufügen fehlgeschlagen.");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2 min-h-[30px]">
        <h4 className="text-sm font-semibold text-app-text flex-1 min-w-0">Aus Projekt-Dokumenten</h4>
        {selected.size > 0 ? (
          <button
            type="button"
            onClick={() => void handleAdd()}
            disabled={adding}
            className="inline-flex items-center gap-1.5 rounded-lg bg-pipedrive-blue px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-pipedrive-blue/90 disabled:opacity-50"
          >
            {adding ? <LoadingSpinner size="sm" /> : <Plus className="w-3.5 h-3.5" />}
            Hinzufügen ({selected.size})
          </button>
        ) : null}
      </div>

      <div className="space-y-4">
        {loading ? (
          <InlineLoadingState label="Dokumente werden geladen…" className="py-6" />
        ) : docs.length === 0 ? (
          <p className="text-sm text-app-text-subtle italic py-4 text-center">
            {imagesOnly
              ? 'Keine Bilder am Projekt (nur PNG, JPEG).'
              : 'Keine anhängbaren Dokumente am Projekt (nur PDF, PNG, JPEG).'}
          </p>
        ) : (
          <>
            {pdfs.length > 0 ? (
              <section>
                <div className="flex items-center gap-2 mb-1.5">
                  <h5 className="text-xs font-semibold text-app-text-subtle">PDF</h5>
                  <span className="text-xs text-slate-400">{pdfs.length}</span>
                </div>
                <ul className="space-y-0.5">
                  {pdfs.map((doc) => {
                    const checked = selected.has(doc.id);
                    const attached = isAttached(doc);
                    return (
                      <li key={doc.id}>
                        <button
                          type="button"
                          onClick={() => !attached && toggle(doc.id)}
                          disabled={attached}
                          aria-disabled={attached}
                          title={attached ? "Bereits angehängt" : undefined}
                          className={`w-full flex items-center gap-3 rounded-md px-2 py-2 text-left transition-colors ${
                            attached
                              ? "bg-app-surface-muted opacity-60 cursor-not-allowed"
                              : checked
                                ? "bg-sky-50"
                                : "bg-app-surface hover:bg-app-hover"
                          }`}
                        >
                          <span
                            className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                              checked || attached ? "bg-pipedrive-blue border-pipedrive-blue text-white" : "border-app-border-strong bg-app-surface"
                            }`}
                          >
                            {checked || attached ? <Check className="w-3.5 h-3.5" /> : null}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-app-text truncate">{doc.filename}</span>
                            <span className="block text-xs text-app-text-subtle">
                              {formatLeadDocumentSize(doc.size)}
                              {attached ? " · bereits angehängt" : ""}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}

            {images.length > 0 ? (
              <section>
                <div className="flex items-center gap-2 mb-1.5">
                  <h5 className="text-xs font-semibold text-app-text-subtle">Bilder</h5>
                  <span className="text-xs text-slate-400">{images.length}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {images.map((doc) => {
                    const checked = selected.has(doc.id);
                    const attached = isAttached(doc);
                    const url = `/api/leads/${encodeURIComponent(leadRouteId)}/documents/${encodeURIComponent(doc.id)}`;
                    return (
                      <button
                        key={doc.id}
                        type="button"
                        onClick={() => !attached && toggle(doc.id)}
                        disabled={attached}
                        aria-disabled={attached}
                        title={attached ? "Bereits angehängt" : undefined}
                        className={`group relative rounded-lg border overflow-hidden text-left transition-colors bg-app-surface ${
                          attached
                            ? "border-app-border opacity-60 cursor-not-allowed"
                            : checked
                              ? "border-pipedrive-blue ring-2 ring-pipedrive-blue/30"
                              : "border-app-border hover:border-app-border-strong"
                        }`}
                      >
                        <span className="block w-full aspect-[4/3] bg-app-surface-muted overflow-hidden">
                          <img src={url} alt={doc.filename} className="w-full h-full object-cover" />
                        </span>
                        {attached ? (
                          <span className="absolute top-2 right-2 rounded-full bg-slate-800/80 px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm">
                            Angehängt
                          </span>
                        ) : null}
                        <span
                          className={`absolute top-2 left-2 w-5 h-5 rounded border flex items-center justify-center shadow-sm ${
                            checked || attached ? "bg-pipedrive-blue border-pipedrive-blue text-white" : "border-app-border-strong bg-app-surface/90"
                          }`}
                        >
                          {checked || attached ? <Check className="w-3.5 h-3.5" /> : null}
                        </span>
                        <span className="block p-2">
                          <span className="block text-xs font-medium text-app-text truncate" title={doc.filename}>
                            {doc.filename}
                          </span>
                          <span className="block text-[11px] text-app-text-subtle">{formatLeadDocumentSize(doc.size)}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}
          </>
        )}

        {notice ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{notice}</div>
        ) : null}

        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        ) : null}
      </div>
    </div>
  );
};
